import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { IMAGES, bgStyle } from '@/config/images';
import { 
  Home, 
  Calculator, 
  Settings2, 
  Zap, 
  AlertTriangle, 
  ArrowRight,
  CheckCircle,
  Target,
  RefreshCw,
  ChevronRight,
  Shield,
  Wind,
  Focus,
  Layers,
  Play,
  Eye
} from 'lucide-react';

const LaserIntroduction = () => {
  const navigate = useNavigate();

  const handleProceed = () => {
    navigate('/laser');
  };

  const steps = [
    {
      number: 1,
      title: 'Select & Prepare Your Material',
      description: 'Choose the right material for your project. Ensure it\'s flat, clean, and free of warping. Check material compatibility with your laser type (CO2 or Diode).',
      icon: Layers,
      tips: ['Check material safety - some plastics release toxic fumes', 'Use masking tape on acrylic to prevent flashback marks']
    },
    {
      number: 2,
      title: 'Place and Secure on Bed',
      description: 'Position your material on the laser bed. Use hold-down pins, magnets, or a honeycomb bed to keep it flat and secure during operation.',
      icon: Target,
      tips: ['Ensure material is perfectly flat', 'Leave clearance for air assist nozzle']
    },
    {
      number: 3,
      title: 'Set Focus Height',
      description: 'Adjust the focus height using your laser\'s focusing tool. Proper focus is critical for clean cuts and crisp engravings.',
      icon: Focus,
      tips: ['Use the focus gauge or autofocus feature', 'Check focus at multiple points for uneven materials']
    },
    {
      number: 4,
      title: 'Configure Air Assist',
      description: 'Enable air assist for cutting operations. This blows debris away, prevents flare-ups, and improves cut quality.',
      icon: Wind,
      tips: ['Higher pressure for thicker materials', 'Reduce pressure for delicate engravings']
    },
    {
      number: 5,
      title: 'Load File & Position Job Origin',
      description: 'Import your design file (SVG, DXF, or image) and position the job origin. Frame the job to verify placement before cutting.',
      icon: Target,
      tips: ['Use corner or center origin based on your workflow', 'Always frame before running']
    },
    {
      number: 6,
      title: 'Run Test Grid',
      description: 'Before your final job, run a small test grid to dial in power and speed settings for your specific material batch.',
      icon: CheckCircle,
      tips: ['Test on scrap material from the same batch', 'Document successful settings for future reference']
    },
    {
      number: 7,
      title: 'Check Ventilation & Safety',
      description: 'Ensure your exhaust system is running and safety interlocks are engaged. Never operate without proper ventilation.',
      icon: Shield,
      tips: ['Check exhaust airflow before starting', 'Wear appropriate eye protection for your laser wavelength']
    },
    {
      number: 8,
      title: 'Begin Engraving/Cutting',
      description: 'Start your job and monitor progress. Never leave the laser unattended during operation.',
      icon: Play,
      tips: ['Stay nearby with fire safety equipment', 'Pause if you notice excessive smoke or flames']
    },
    {
      number: 9,
      title: 'Cooldown & Inspect',
      description: 'Allow material to cool before handling. Inspect cut edges and engraving quality.',
      icon: Eye,
      tips: ['Check for incomplete cuts', 'Verify engraving depth is consistent']
    },
    {
      number: 10,
      title: 'Cleaning & Post-Processing',
      description: 'Clean the lens and mirrors regularly. Remove any debris from the honeycomb bed. Sand or finish your workpiece as needed.',
      icon: RefreshCw,
      tips: ['Clean lens after every session', 'Log your settings for future projects']
    }
  ];

  const features = [
    {
      icon: Calculator,
      title: 'Laser Calculator',
      description: 'Get optimal power, speed, and pass settings for any material',
      link: '/laser/calculator'
    },
    {
      icon: AlertTriangle,
      title: 'Troubleshooting',
      description: 'Fix alignment issues, power problems, and cut quality',
      link: '/laser/troubleshooting'
    },
    {
      icon: Settings2,
      title: 'Machine Setup',
      description: 'Configure your CO2 or diode laser profile',
      link: '/laser/setup'
    },
    {
      icon: Home,
      title: 'Dashboard',
      description: 'Your laser command center with quick access to all tools',
      link: '/laser'
    }
  ];

  return (
    <div 
      data-testid="laser-introduction-page" 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.GETTING_STARTED)}
    >
      {/* Dark Overlay */}
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
      
      <div className="max-w-4xl mx-auto space-y-8 pb-12">
        {/* Header */}
        <div className="text-center pt-6">
          <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Zap className="w-4 h-4" />
            CNC Laser Edition
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Step-by-Step Laser Guide
          </h1>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            Master laser cutting and engraving with our comprehensive workflow guide. 
            From material selection to finished piece.
          </p>
        </div>

        {/* Safety Warning */}
        <div className="bg-red-900/60 border border-red-500/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-400 mb-1">Safety First</h3>
              <p className="text-sm text-zinc-300">
                Laser operation requires proper safety equipment. Always wear appropriate eye protection, 
                ensure adequate ventilation, and never leave the machine unattended during operation.
              </p>
            </div>
          </div>
        </div>

        {/* Step-by-Step Workflow */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-orange-500" />
            Complete Laser Workflow
          </h2>
          
          <div className="space-y-3">
            {steps.map((step, index) => (
              <div 
                key={step.number}
                className="bg-zinc-900/95 border border-zinc-700/50 rounded-lg p-4 hover:border-orange-500/50 transition-colors"
              >
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                      <span className="text-orange-400 font-bold">{step.number}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <step.icon className="w-4 h-4 text-orange-400" />
                      <h3 className="font-semibold text-white">{step.title}</h3>
                    </div>
                    <p className="text-sm text-zinc-400 mb-2">{step.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {step.tips.map((tip, tipIdx) => (
                        <span 
                          key={tipIdx}
                          className="inline-flex items-center gap-1 text-xs bg-zinc-800/50 text-zinc-300 px-2 py-1 rounded"
                        >
                          <CheckCircle className="w-3 h-3 text-green-400" />
                          {tip}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Features Overview */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-500" />
            What's Inside
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {features.map((feature) => (
              <Link 
                key={feature.title}
                to={feature.link}
                className="bg-zinc-900/95 border border-zinc-700/50 rounded-lg p-4 hover:border-orange-500/50 transition-colors group"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-orange-500/20">
                    <feature.icon className="w-5 h-5 text-orange-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white group-hover:text-orange-400 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-zinc-400">{feature.description}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-orange-400 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Laser Types */}
        <div className="bg-zinc-900/95 border border-zinc-700/50 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-500" />
            Supported Laser Types
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-zinc-800/50 rounded-lg">
              <h3 className="font-semibold text-blue-400 mb-2">CO2 Lasers</h3>
              <p className="text-sm text-zinc-400 mb-2">
                Ideal for cutting and engraving non-metallic materials like wood, acrylic, leather, 
                fabric, and paper. Common power ranges: 40W-150W.
              </p>
              <div className="flex flex-wrap gap-1">
                <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded">Wood</span>
                <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded">Acrylic</span>
                <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded">Leather</span>
                <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded">Paper</span>
              </div>
            </div>
            <div className="p-4 bg-zinc-800/50 rounded-lg">
              <h3 className="font-semibold text-purple-400 mb-2">Diode Lasers</h3>
              <p className="text-sm text-zinc-400 mb-2">
                Great for engraving and light cutting. Can mark some metals directly. 
                Common power ranges: 5W-20W optical output.
              </p>
              <div className="flex flex-wrap gap-1">
                <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded">Wood</span>
                <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded">Leather</span>
                <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded">Anodized Metal</span>
                <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded">Tile</span>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center pt-4">
          <Button
            onClick={handleProceed}
            data-testid="get-started-btn"
            className="bg-[#e67e22] hover:bg-[#d35400] text-white font-bold px-8 py-3 text-base uppercase tracking-wide shadow-[0_0_15px_rgba(230,126,34,0.4)] hover:shadow-[0_0_25px_rgba(230,126,34,0.5)]"
          >
            Go to Dashboard
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LaserIntroduction;
